import React, { useMemo, useState, useCallback, useEffect } from "react";
import cc from "classcat";
import type { RewardOffer } from "byzantine/src/dbbl/businessLogic/entities/cardLinkedOffers";
import { CardOfferScrollViewProps } from "./types";
import OfferCard from "./CardOfferTile";
import styles from "./CardOfferScrollView.module.scss";
import useDragHorizontalScroll from "../../hooks/useDragHorizontalScroll";

const CardOfferScrollView: React.FC<CardOfferScrollViewProps> = ({
  offers,
  onOfferClick,
  maxCards,
  className,
}) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const displayedOffers = useMemo(
    () => (maxCards !== undefined ? offers.slice(0, maxCards) : offers),
    [offers, maxCards]
  );

  const containerClasses = useMemo(
    () => (className ? `${styles.container} ${className}` : styles.container),
    [className]
  );

  const leftShadowClasses = useMemo(
    () => `${styles.shadowLeft} ${canScrollLeft ? styles.visible : ""}`.trim(),
    [canScrollLeft]
  );

  const rightShadowClasses = useMemo(
    () => `${styles.shadowRight} ${canScrollRight ? styles.visible : ""}`.trim(),
    [canScrollRight]
  );

  const {
    ref: offersScrollRef,
    isDragging,
    handlers,
  } = useDragHorizontalScroll<HTMLDivElement>({
    dragThresholdPx: 6,
    translateVerticalWheel: true,
    enableOnTouch: false,
  });

  const checkScrollState = useCallback(() => {
    const element = offersScrollRef.current;
    if (!element) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const maxScrollLeft = scrollWidth - clientWidth;

    setCanScrollLeft(scrollLeft > 1); // Small threshold for precision
    setCanScrollRight(scrollLeft < maxScrollLeft - 1);
  }, [offersScrollRef]);

  // Check scroll state when offers change
  useEffect(() => {
    checkScrollState();
  }, [displayedOffers, checkScrollState]);

  useEffect(() => {
    const element = offersScrollRef.current;
    if (!element) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      checkScrollState();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkScrollState, offersScrollRef]);

  const offersListClasses = cc([
    styles.offersList,
    isDragging && styles.dragging,
  ]);

  if (offers.length === 0) {
    return null;
  }

  return (
    <>
      <div className={containerClasses} data-testid="cashback-offer-view">
        {/* Left shadow overlay */}
        <div className={leftShadowClasses} />

        {/* Right shadow overlay */}
        <div className={rightShadowClasses} />

        <div
          className={offersListClasses}
          ref={offersScrollRef}
          onScroll={checkScrollState}
          {...handlers}
        >
          <div className={styles.offersTrack}>
            {displayedOffers.map((offer: RewardOffer) => (
              <OfferCard key={offer.id} offer={offer} onClick={onOfferClick} />
            ))}
          </div>
        </div>
      </div>
      <div className={styles.padding} />
    </>
  );
};

export default CardOfferScrollView;
